/**
 * Org Detail / Activity tab.
 *
 * Server `/api/audit/logs` doesn't have an `objectId` filter (only
 * `objectType` + `search`). For the org-scoped view we use
 * `search: organisation.id` — the org GUID appears in title /
 * objectName / changes for any related action — and cap at 25
 * entries with a "see full logs" link to /ops/logs for power users.
 *
 * If the org-id-only filter misses some legitimate entries (e.g. an
 * invoice action keyed only by invoice id), 53c.5 polish or a backend
 * follow-up can add an explicit `relatedOrgId` index.
 */
import { useEffect, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { EmptyState, ErrorState, LoadingSkeleton } from '@repro/ui';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { api } from '../../../utils/api';
import type { OrgDetailContext } from '../OrgDetail';

const PAGE_SIZE = 25;

interface AuditLogRow {
  id: string;
  title: string;
  action: 'CREATE' | 'UPDATE' | 'DELETE' | 'SYNC' | 'CLEANUP';
  objectType: string;
  objectId: string;
  objectName?: string;
  userEmail?: string;
  createdAt: string;
}

export default function Activity() {
  const { organisation } = useOutletContext<OrgDetailContext>();

  const [logs, setLogs] = useState<AuditLogRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .getAuditLogs({ search: organisation.id, page: 1, limit: PAGE_SIZE })
      .then((result) => {
        if (cancelled) return;
        setLogs(result.logs as AuditLogRow[]);
        setTotal(result.total);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load activity');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [organisation.id]);

  if (loading) {
    return (
      <div className="mt-4">
        <LoadingSkeleton rows={6} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-4">
        <ErrorState title="Couldn't load activity" body={error} />
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="mt-4">
        <EmptyState
          title="No recent activity"
          body="No audit log entries reference this organisation yet."
        />
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-3">
      <div className="bg-base-100 shadow rounded-lg overflow-hidden">
        <table className="table w-full">
          <thead>
            <tr>
              <th>When</th>
              <th>Action</th>
              <th>Object</th>
              <th>Title</th>
              <th>Actor</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((entry) => (
              <tr key={entry.id}>
                <td className="text-sm text-base-content/70 whitespace-nowrap">
                  {formatDateTime(entry.createdAt)}
                </td>
                <td>
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${actionClass(
                      entry.action
                    )}`}
                  >
                    {entry.action}
                  </span>
                </td>
                <td className="text-sm">
                  <div>{entry.objectType}</div>
                  {entry.objectName && (
                    <div className="text-xs text-base-content/60">{entry.objectName}</div>
                  )}
                </td>
                <td className="text-sm">{entry.title}</td>
                <td className="text-sm text-base-content/70">{entry.userEmail ?? 'system'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between text-xs text-base-content/60">
        <span>
          Showing {logs.length} of {total} matching entries.
        </span>
        <Link to="/ops/logs" className="btn btn-ghost btn-xs gap-1">
          <span>Full logs</span>
          <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
        </Link>
      </div>
    </div>
  );
}

function actionClass(action: AuditLogRow['action']): string {
  switch (action) {
    case 'CREATE':
      return 'bg-green-100 text-green-800';
    case 'UPDATE':
      return 'bg-blue-100 text-blue-800';
    case 'DELETE':
      return 'bg-red-100 text-red-800';
    case 'SYNC':
      return 'bg-purple-100 text-purple-800';
    case 'CLEANUP':
      return 'bg-yellow-100 text-yellow-800';
    default:
      return 'bg-base-200 text-base-content/70';
  }
}

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}
